import axios from 'axios';

// API Base URL configuration
// Priority: REACT_APP_BACKEND_URL env var > relative paths (same-origin)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Log configuration once for debugging (non-production only)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  console.log('[API] Base URL:', API_BASE || '(relative paths - same-origin)');
}

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth
export const signup = (data) => api.post('/auth/signup', data);
export const login = (data) => api.post('/auth/login', data);
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/me');

// Dual-Role
export const addRole = (profileType) => api.post('/me/add-role', { profileType });
export const switchRole = (profileType) => api.post('/me/switch-role', { profileType });

// Unified Profile (for onboarding wizard)
export const getMyProfile = () => api.get('/me/profile');
export const updateMyProfile = (data) => api.put('/me/profile', data);
export const publishMyProfile = () => api.post('/me/profile/publish');

// Profiles
export const getProfile = (id) => api.get(`/profiles/${id}`);

// Musicians
export const getMusicians = (params = {}) => api.get('/musicians', { params });
export const getMyMusicianProfile = () => api.get('/musicians/me');
export const updateMyMusicianProfile = (data) => api.put('/musicians/me', data);
export const updateMyGear = (gear) => api.put('/musicians/me/gear', gear);
export const getMyMusicianAvailability = () => api.get('/musicians/me/availability');
export const updateMyMusicianAvailability = (data) => api.put('/musicians/me/availability', data);

// Tech
export const getTech = (params = {}) => api.get('/tech', { params });
export const getMyTechProfile = () => api.get('/tech/me');
export const updateMyTechProfile = (data) => api.put('/tech/me', data);
export const getMyTechAvailability = () => api.get('/tech/me/availability');
export const updateMyTechAvailability = (data) => api.put('/tech/me/availability', data);

// Venues
export const getVenues = () => api.get('/venues');
export const getMyVenueProfile = () => api.get('/venues/me');
export const updateMyVenueProfile = (data) => api.put('/venues/me', data);
export const uploadMyVenuePhoto = (formData) => api.post('/venues/me/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteMyVenuePhoto = () => api.delete('/venues/me/photo');
// A path the backend hands out (a venue's photoUrl, "/api/..."), made loadable from the page:
// prefixed with the API host where the frontend and backend live on different hosts.
export const apiAssetUrl = (path) => (path && path.startsWith('/') ? `${API_BASE}${path}` : path || null);

// Venue rooms — equipment belongs to a room, not a venue
export const getMyVenueRooms = () => api.get('/venues/me/rooms');
export const getVenueRooms = (venueId) => api.get(`/venues/${venueId}/rooms`);
export const createVenueRoom = (data) => api.post('/venues/me/rooms', data);
export const updateVenueRoom = (roomId, data) => api.put(`/venues/me/rooms/${roomId}`, data);
export const deleteVenueRoom = (roomId) => api.delete(`/venues/me/rooms/${roomId}`);

// Venue Inventory (rider-check equipment), scoped to a room
export const getVenueInventory = (roomId) => api.get(`/venues/me/rooms/${roomId}/inventory`);
export const createVenueInventoryItem = (roomId, data) => api.post(`/venues/me/rooms/${roomId}/inventory`, data);
export const updateVenueInventoryItem = (itemId, data) => api.put(`/venues/me/inventory/${itemId}`, data);
export const deleteVenueInventoryItem = (itemId) => api.delete(`/venues/me/inventory/${itemId}`);
export const confirmVenueInventory = (roomId) => api.post(`/venues/me/rooms/${roomId}/inventory/confirm-all`);

// Rider Check v2 (named rider variants + real matching engine)
export const getRiderCheckTaxonomy = () => api.get('/rider-check/taxonomy');
export const getMyStructuredRiders = () => api.get('/artists/me/riders');
export const getMyStructuredRider = (variantId) => api.get(`/artists/me/riders/${variantId}`);
export const createMyStructuredRider = (name, lines = []) => api.post('/artists/me/riders', { name, lines });
export const saveMyStructuredRider = (variantId, lines, name) => api.put(`/artists/me/riders/${variantId}`, { lines, name });
export const deleteMyStructuredRider = (variantId) => api.delete(`/artists/me/riders/${variantId}`);
export const runRiderCheck = (roomId, riderVariantId) => api.post('/rider-check/run', { room_id: roomId, rider_variant_id: riderVariantId });

// Requests
export const getRequests = (params = {}) => api.get('/requests', { params });
export const getRequest = (id) => api.get(`/requests/${id}`);
export const getRequestBySlug = (slug) => api.get(`/gig/${slug}`);
export const createRequest = (data) => api.post('/venues/requests', data);
export const getMyVenueRequests = () => api.get('/venues/requests');
export const updateRequestStatus = (id, status) => api.put(`/venues/requests/${id}/status?status=${status}`);
export const editRequest = (id, data) => api.put(`/venues/requests/${id}`, data);

// Interest
export const addInterest = (requestId) => api.post(`/requests/${requestId}/interest`);
export const removeInterest = (requestId) => api.delete(`/requests/${requestId}/interest`);
export const getMyInterests = () => api.get('/my-interests');
export const updateInterestStatus = (requestId, interestId, status) => 
  api.put(`/venues/requests/${requestId}/interests/${interestId}/status?status=${status}`);
export const updateInterestTravel = (interestId, data) => 
  api.put(`/interests/${interestId}/travel`, data);
export const getInterestDetail = (interestId) => api.get(`/interests/${interestId}`);
export const getRequestInterests = (requestId) => api.get(`/venues/requests/${requestId}/interests`);

// Bookings (WhatsApp Unlock Flow)
export const createBooking = (data) => api.post('/bookings', data);
export const getMyBookings = () => api.get('/bookings');
export const getBooking = (bookingId) => api.get(`/bookings/${bookingId}`);
export const updateBooking = (bookingId, data) => api.put(`/bookings/${bookingId}`, data);
// gear: array of {category, brings, notes} to override profile defaults, or null to clear
export const updateBookingGearOverride = (bookingId, gear) => api.put(`/bookings/${bookingId}/gear-override`, gear);
export const confirmBooking = (bookingId) => api.post(`/bookings/${bookingId}/confirm`);
export const cancelBooking = (bookingId) => api.post(`/bookings/${bookingId}/cancel`);
export const getBookingAttentionCount = () => api.get('/bookings/attention-count');
export const exportBookingCalendar = (bookingId) => api.get(`/bookings/${bookingId}/calendar`, { responseType: 'blob' });

// Booking sub-documents
export const getRunOfShow = (bookingId) => api.get(`/bookings/${bookingId}/run-of-show`);
export const updateRunOfShow = (bookingId, slots) => api.put(`/bookings/${bookingId}/run-of-show`, { slots });
export const getCrewSlots = (bookingId) => api.get(`/bookings/${bookingId}/crew-slots`);
export const updateCrewSlots = (bookingId, slots) => api.put(`/bookings/${bookingId}/crew-slots`, { slots });
export const confirmCrewSlot = (bookingId, slotId) => api.put(`/bookings/${bookingId}/crew-slots/${slotId}/confirm`);
export const getGuestList = (bookingId) => api.get(`/bookings/${bookingId}/guest-list`);
export const updateGuestList = (bookingId, data) => api.put(`/bookings/${bookingId}/guest-list`, data);

// Demo seed
export const seedDemo = () => api.post('/demo/seed');

// Invites (Structured Confirmation Flow)
export const createInvite = (data) => api.post('/invites', data);
export const getPendingInvites = () => api.get('/invites/pending');
export const getInvite = (inviteId) => api.get(`/invites/${inviteId}`);
export const respondToInvite = (inviteId, action, declineReason = null) => 
  api.post(`/invites/${inviteId}/respond`, { action, declineReason });
export const cancelInvite = (inviteId) => api.post(`/invites/${inviteId}/cancel`);
export const getRequestInvites = (requestId) => api.get(`/requests/${requestId}/invites`);
export const getInviteCount = () => api.get('/invites/count');

// Bookmarks
export const toggleBookmark = (requestId) => api.post(`/requests/${requestId}/bookmark`);
export const getMyBookmarks = () => api.get('/my-bookmarks');
export const getMyBookmarkedRequests = () => api.get('/my-bookmarks/requests');

// Admin
export const adminHideProfile = (profileId) => api.post(`/admin/profiles/${profileId}/hide`);
export const adminUnhideProfile = (profileId) => api.post(`/admin/profiles/${profileId}/unhide`);
export const adminHideRequest = (requestId) => api.post(`/admin/requests/${requestId}/hide`);
export const adminUnhideRequest = (requestId) => api.post(`/admin/requests/${requestId}/unhide`);

// Travel Estimation
export const estimateTravel = (data) => api.post('/travel/estimate', data);

// ICS Generation
export const downloadTravelICS = (data) => api.post('/ics/travel', data, { responseType: 'blob' });

// Waitlist (pre-launch coming-soon signups)
export const joinWaitlist = (data) => api.post('/waitlist', data);
export const downloadGigICS = (data) => api.post('/ics/gig', data, { responseType: 'blob' });
export const downloadBothICS = (data) => api.post('/ics/both', data, { responseType: 'blob' });

// Riders
export const uploadRider = (formData) => api.post('/riders/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getMyRiders = () => api.get('/riders/me');
export const getProfileRiders = (profileId) => api.get(`/riders/profile/${profileId}`);
export const downloadRider = (riderId) => `${API_BASE}/api/riders/file/${riderId}`;
export const deleteRider = (riderId) => api.delete(`/riders/${riderId}`);
export const updateRider = (riderId, data) => api.put(`/riders/${riderId}`, data);

export default api;
